import styled from '@emotion/styled';
import Head from 'next/head';
import React from 'react';
import SignUpForm from '../../common-components/component-library/forms/SignUpForm';
import Content from '../../common-components/component-library/layout/Content';
import Hero from '../../common-components/component-library/page-sections/Hero';
import RichText from '../../common-components/contentful-elements/RichText/RichText';
import { IStaticPageFields } from '../../types/@types/generated/contentful';
import { getClient } from '../../utils/contentfulSetup';
import { UrlError } from '../../utils/error';
import { breakpoints, colors } from '../../utils/styleguide';
import { captureException } from '../../utils/tracking/sentry/sentry';
import withAppProps from '../../utils/withAppProps';

const Wrapper = styled.article`
  margin: 20px;
  ${breakpoints.tablet} {
    margin: 80px 90px;
  }
`;

const Page: React.FunctionComponent<IStaticPageFields> = ({
  pageContent,
  title,
  preamble,
  showSignUpBox,
  centerPageContent,
  longPreamble,
  heroTextColor,
  heroBackgroundColor,
  slug,
}) => (
  <>
    <Head>
      <title>{title}</title>
      <meta key="og:title" name="og:title" content={title} />
      <meta key="description" name="description" content={preamble} />
      <meta key="og:description" name="og:description" content={preamble} />
      <link rel="canonical" href={`https://www.milkywire.com/page/${slug}`} />
    </Head>
    <Hero
      title={title}
      body={preamble}
      longBody={longPreamble}
      textColor={heroTextColor ? colors[heroTextColor] : colors.white}
      backgroundColor={heroBackgroundColor ? colors[heroBackgroundColor] : colors.petrol}
    />
    <Wrapper>
      <Content>
        <RichText document={pageContent} centered={centerPageContent} useBuiltInMargins />
        {showSignUpBox && <SignUpForm />}
      </Content>
    </Wrapper>
  </>
);

export const getServerSideProps = withAppProps(async ({ query, res }) => {
  try {
    const entries = await getClient().getEntries<IStaticPageFields>({
      content_type: 'staticPage',
      'fields.slug[in]': query.slug,
    });

    if (!entries.items.length) {
      throw new UrlError('Page not found', { status: 404 });
    }

    const page = entries.items[0].fields;

    return { props: { ...page, layoutProps: { floatingHeader: true } } };
  } catch (error) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    const statusCode = error.statusCode || 404;

    if (res) {
      res.statusCode = statusCode;
    }

    captureException(error);

    // @ts-expect-error type error
    error.statusCode = statusCode;
    throw error;
  }
});

export default Page;
